import type {
  Quiz,
  WordConnectPlayStart,
  WordConnectPlayState,
  WordConnectCellDto,
  WordConnectWordDto,
  WordConnectLetterSlotDto,
} from '../services/api';

export function isWordConnectQuizFormat(quiz: Quiz | null | undefined): boolean {
  const f = (quiz?.format ?? '').toString().toLowerCase();
  return f === 'wordconnect';
}

export function parseWordConnectPlayStart(raw: unknown): WordConnectPlayStart | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const playSessionId = String(o.playSessionId ?? o.PlaySessionId ?? '').trim();
  if (!playSessionId) return null;

  const words = parseWords(o.words ?? o.Words);
  const cells = parseCells(o.cells ?? o.Cells);
  const letterPool = parseLetterPool(o.letterPool ?? o.LetterPool);

  const engineBootstrapCipher = String(
    o.engineBootstrapCipher ?? o.EngineBootstrapCipher ?? ''
  ).trim();

  const activeQuestionIds = parseGuidList(o.activeQuestionIds ?? o.ActiveQuestionIds);

  return {
    playSessionId,
    engineBootstrapCipher: engineBootstrapCipher || undefined,
    activeQuestionIds: activeQuestionIds.length > 0 ? activeQuestionIds : undefined,
    activeWordCount: Number(o.activeWordCount ?? o.ActiveWordCount ?? words.length) || words.length,
    initialTimerSeconds:
      Number(o.initialTimerSeconds ?? o.InitialTimerSeconds ?? 60) || 60,
    timeBonusPerLetterSeconds:
      Number(o.timeBonusPerLetterSeconds ?? o.TimeBonusPerLetterSeconds ?? 3) || 3,
    timeBonusPerWordSeconds:
      Number(o.timeBonusPerWordSeconds ?? o.TimeBonusPerWordSeconds ?? 10) || 10,
    pointsPerWord: Number(o.pointsPerWord ?? o.PointsPerWord ?? 6) || 6,
    words,
    cells,
    letterPool,
  };
}

export function parseWordConnectPlayState(raw: unknown): WordConnectPlayState {
  if (!raw || typeof raw !== 'object') {
    return {
      words: [],
      cells: [],
      letterPool: [],
      totalScore: 0,
      wordsCompleted: 0,
      activeWordCount: 0,
      sessionComplete: false,
      wordJustCompleted: false,
      pointsEarnedThisWord: 0,
      pointsPerWordSlot: [],
      placementAccepted: true,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    words: parseWords(o.words ?? o.Words),
    cells: parseCells(o.cells ?? o.Cells),
    letterPool: parseLetterPool(o.letterPool ?? o.LetterPool),
    totalScore: Number(o.totalScore ?? o.TotalScore ?? 0),
    wordsCompleted: Number(o.wordsCompleted ?? o.WordsCompleted ?? 0),
    activeWordCount: Number(o.activeWordCount ?? o.ActiveWordCount ?? 0),
    sessionComplete: Boolean(o.sessionComplete ?? o.SessionComplete),
    wordJustCompleted: Boolean(o.wordJustCompleted ?? o.WordJustCompleted),
    pointsEarnedThisWord: Number(o.pointsEarnedThisWord ?? o.PointsEarnedThisWord ?? 0),
    pointsPerWordSlot: Array.isArray(o.pointsPerWordSlot ?? o.PointsPerWordSlot)
      ? (o.pointsPerWordSlot ?? o.PointsPerWordSlot) as number[]
      : [],
    placementAccepted: o.placementAccepted !== false && o.PlacementAccepted !== false,
    placementMessage:
      o.placementMessage != null
        ? String(o.placementMessage)
        : o.PlacementMessage != null
          ? String(o.PlacementMessage)
          : null,
  };
}

function parseGuidList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((id) => String(id ?? '').trim())
    .filter((id) => id.length > 0);
}

function parseWords(raw: unknown): WordConnectWordDto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((w): WordConnectWordDto | null => {
      if (!w || typeof w !== 'object') return null;
      const o = w as Record<string, unknown>;
      const questionId = String(o.questionId ?? o.QuestionId ?? '').trim();
      const text = String(o.text ?? o.Text ?? '').trim();
      const length = Number(o.length ?? o.Length ?? text.length) || text.length;
      if (!questionId || length < 1) return null;
      return {
        questionId,
        text: '',
        length,
        startRow: Number(o.startRow ?? o.StartRow ?? 0),
        startCol: Number(o.startCol ?? o.StartCol ?? 0),
        isHorizontal: Boolean(o.isHorizontal ?? o.IsHorizontal ?? true),
        isCompleted: Boolean(o.isCompleted ?? o.IsCompleted ?? false),
      };
    })
    .filter((w): w is WordConnectWordDto => w != null);
}

function parseCells(raw: unknown): WordConnectCellDto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c): WordConnectCellDto | null => {
      if (!c || typeof c !== 'object') return null;
      const o = c as Record<string, unknown>;
      const wordColorRaw = o.wordColorIndex ?? o.WordColorIndex;
      return {
        row: Number(o.row ?? o.Row ?? 0),
        col: Number(o.col ?? o.Col ?? 0),
        letter: o.letter != null ? String(o.letter) : o.Letter != null ? String(o.Letter) : null,
        wordColorIndex:
          wordColorRaw === null || wordColorRaw === undefined
            ? null
            : Number(wordColorRaw),
      };
    })
    .filter((c): c is WordConnectCellDto => c != null);
}

function parseLetterPool(raw: unknown): WordConnectLetterSlotDto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s): WordConnectLetterSlotDto | null => {
      if (!s || typeof s !== 'object') return null;
      const o = s as Record<string, unknown>;
      const slotId = String(o.slotId ?? o.SlotId ?? '').trim();
      const letter = String(o.letter ?? o.Letter ?? '').trim();
      if (!slotId || !letter) return null;
      return {
        slotId,
        letter,
        sourceQuestionId: String(o.sourceQuestionId ?? o.SourceQuestionId ?? '').trim() || undefined,
        wordColorIndex: Number(o.wordColorIndex ?? o.WordColorIndex ?? 0),
      };
    })
    .filter((s): s is WordConnectLetterSlotDto => s != null);
}
